/**
 * Manages async operations to ensure only the most recent invocation's result
 * gets committed. Useful for preventing race conditions where an older async
 * operation might complete after a newer one (e.g., rapid user input triggering
 * multiple data fetches).
 *
 * @example
 * const gate = new LatestGate();
 * // Only the result of the latest fetch will be committed to this.data
 * gate.runLatest(this, 'data', () => fetchData(query), (result) => { this.data = result; });
 */
export class LatestGate {
  /** Tracks version numbers per object/property pair. Uses WeakMap to avoid memory leaks. */
  private versions = new WeakMap<object, Map<PropertyKey, number>>();

  /**
   * Increments and returns the next version number for a given object/property pair.
   *
   * @param obj - The object being tracked
   * @param prop - The property key identifying which async operation
   * @returns The new version number
   */
  private nextVersion(obj: object, prop: PropertyKey) {
    let m = this.versions.get(obj);
    if (!m) this.versions.set(obj, (m = new Map()));
    const v = (m.get(prop) ?? 0) + 1;
    m.set(prop, v);
    return v;
  }

  /**
   * Checks if the given version is still the latest for the object/property pair.
   *
   * @param obj - The object being tracked
   * @param prop - The property key
   * @param v - The version to check
   * @returns True if v is the current latest version
   */
  private isLatest(obj: object, prop: PropertyKey, v: number) {
    return this.versions.get(obj)?.get(prop) === v;
  }

  /**
   * Executes an async task and only commits the result if no newer task
   * has been started for the same object/property pair.
   *
   * @param obj - The object to track versions against
   * @param prop - A property key to identify this particular async operation (can be any PropertyKey, not just actual properties of obj)
   * @param task - The async task to execute
   * @param commit - Called with the result if this is still the latest invocation
   * @param stale - Optional callback if the result is stale (a newer task was started)
   * @returns The task result (regardless of whether it was committed or stale)
   */
  async runLatest<R>(
    obj: object,
    prop: PropertyKey,
    task: () => Promise<R>,
    commit: (result: R) => void,
    stale?: (result: R) => void
  ): Promise<R> {
    const v = this.nextVersion(obj, prop);
    const result = await task();

    if (this.isLatest(obj, prop, v)) commit(result);
    else stale?.(result);

    return result;
  }
}

/** Shared gate instance for the module-level assignLatest helper. */
const gate = new LatestGate();

/**
 * Executes an async task and assigns the result to obj[prop] only if no newer
 * task has been started for the same object/property pair.
 *
 * @param obj - The object to assign to
 * @param prop - The property to assign
 * @param task - The async task that produces the value
 * @returns The value and whether it was applied (false if a newer task superseded it)
 *
 * @example
 * const { value, applied } = await assignLatest(this, 'texture', () => loadTexture(url));
 */
export async function assignLatest<T extends object, K extends keyof T>(
  obj: T,
  prop: K,
  task: () => Promise<T[K]>
): Promise<{ value: T[K]; applied: boolean }> {
  let applied = false;
  const value = await gate.runLatest(
    obj,
    prop as PropertyKey,
    task,
    (v) => { obj[prop] = v; applied = true; }
  );
  return { value, applied };
}

/**
 * Executes an async task and returns the result only if no newer task has been
 * started for the same object/property pair. Unlike assignLatest, this does not
 * auto-assign to a property—use it when you need to load into a local variable.
 *
 * @param obj - The object to track versions against
 * @param prop - The property key identifying this async operation (can be any PropertyKey, not just actual properties of obj)
 * @param task - The async task that produces the value
 * @returns The value if this is the latest invocation, or undefined if stale
 *
 * @example
 * const texture = await getLatest(this, 'texture', () => loadTexture(url));
 * if (texture !== undefined) {
 *   // use texture as a local variable
 * }
 */
export async function getLatest<R>(
  obj: object,
  prop: PropertyKey,
  task: () => Promise<R>
): Promise<R | undefined> {
  let result: R | undefined;
  await gate.runLatest(
    obj,
    prop,
    task,
    (v) => { result = v; }
  );
  return result;
}
